/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button } from '@msdyn365-commerce-modules/utilities';
import { IVideoPlayerViewProps } from '@msdyn365-commerce-modules/video-player/src/modules/video-player/video-player';
// import { IPlayerResources } from '@msdyn365-commerce/components/dist/types/videoplayer/components/data/player-resources';
import { Image } from '@msdyn365-commerce/core';
import * as React from 'react';
import { IVideoPlayerProps } from '../definition-extensions/video-player.ext.props.autogenerated';

interface IVideoPlayerState {
    isMobile: boolean;
  }

/**
 *
 * VideoPlayer component
 * @extends {React.PureComponent<IVideoPlayerViewProps>}
 */
export class VideoPlayerView extends React.PureComponent<IVideoPlayerViewProps & IVideoPlayerProps<{}>, IVideoPlayerState>  {
    constructor(props: IVideoPlayerViewProps) {
        //@ts-ignore
        super(props);
        this.state = {isMobile: false};
    }

    public componentDidMount(): void {
        this._updateIsMobile();
        window.addEventListener('resize', this._updateIsMobile);
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this._updateIsMobile);
    }

    public render(): JSX.Element | null {
        return (
            <div
                id={this.props.id}
                className='ms-video-player citta-video-wrapper'
                {...this.props.renderModuleAttributes(this.props)}
            >
                <div className='citta-video-content'>
                    {this.state.isMobile ? this._renderMobile() : this._renderDesktop()}
                    {this._renderVideoPlayerOverlay()}
                </div>
            </div>
        );
    }

    private _renderDesktop = (): React.ReactNode | null => {
        const playerResources: any = {
            playAriaLabel: this.props.config.playLabel ? this.props.config.playLabel : '',
            pauseAriaLabel: this.props.config.pauseLabel ? this.props.config.pauseLabel : '',
            muteAriaLabel: this.props.config.muteLabel ? this.props.config.muteLabel : '',
            unMuteAriaLabel: this.props.config.unMuteLabel ? this.props.config.unMuteLabel: '',
            fullScreenAriaLabel: this.props.config.fullScreenLabel ? this.props.config.fullScreenLabel : '',
            exitFullScreenAriaLabel: this.props.config.exitFullScreenLabel ? this.props.config.exitFullScreenLabel : '',
            playingAriaLabel: this.props.config.playingLabel ? this.props.config.playingLabel : '',
            pausedAriaLabel: this.props.config.pausedLabel ? this.props.config.pausedLabel : '',
            ...this.props.resources
        };
        if (this.props.player) {
            return React.cloneElement(this.props.player as React.ReactElement, {playerResources});
        }
        return null;
    };

    private _renderMobile = (): React.ReactNode | null => {
        const imageSettingsDefault = {
            disableLazyLoad: true,
            viewports: {
                xs: { q: `q=80&w=770&h=500&m=6`, w: 770, h: 500 }
            }
        };
        const image = this.props.config.mobileImage;

        if (image) {
            return (
                <div className='citta-video-mobile-wrapper'>
                    {image && <Image
                        className='citta-video-mobile-image'
                        src={image.src || ''}
                        altText={image.altText}
                        gridSettings={this.props.context.request.gridSettings!}
                        imageSettings={ image.imageSettings?.viewports ? image.imageSettings : imageSettingsDefault }
                        loadFailureBehavior='default'
                    />}
                </div>
            );
        }
        return null;
    }

    private _renderVideoPlayerOverlay = (): React.ReactNode | null => {
        const { buttonText, buttonLink, headerText, horizontalPosition } = this.props.config;
        if (buttonText || headerText) {
            return (
                <div className={`citta-video-overlay ${horizontalPosition || ''}`}>
                    { headerText && <h6 className='citta-video-overlay__header'>{headerText}</h6> }
                    { buttonText && <Button
                        title={buttonText}
                        className='citta-video-overlay__button'
                        href={buttonLink}
                        color={this.state.isMobile ? 'link' : 'link-light'}
                    >{buttonText}
                    </Button> }
                </div>
            );
        }
        return null;
    };

    private _updateIsMobile = () => {
        this.setState({
            isMobile: window.innerWidth < 768
        });
    }
}

export default VideoPlayerView;